import React, { useState, useEffect, useContext } from 'react';
import httpService, { storedAccessToken } from '../../services/http';

type Props = {
  children: React.ReactNode;
};

type Licence = {
  name: string;
  available_simulations: number;
  start_date: string;
  end_date: string;
};

type contextType = {
  setLicenseData: () => void;
  /** false for free-tier users (no organisation): the backend returns an empty list */
  hasLicense: boolean;
  name: string;
  /** 0 means unlimited */
  availableSimulations: number;
  startDate: string;
  endDate: string;
};

export const LicenseDataContext: React.Context<contextType | undefined> = React.createContext<contextType | undefined>(
  undefined,
);

const LicenseDataProvider = ({ children }: Props) => {
  const [hasLicense, setHasLicense] = useState(false);
  const [name, setName] = useState('');
  const [availableSimulations, setAvailableSimulations] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  /**
   * @description is called to set license data. The backend returns the organisation's licence pool,
   * one pool for all products and members of the organisation. Free-tier users get an empty list.
   */
  const setLicenseData = () => {
    httpService
      .get('licence/', {}, 'json', true)
      .then((response) => {
        const licences: Licence[] = Array.isArray(response.data) ? response.data : [];
        const license = licences[0];

        if (!license) {
          setHasLicense(false);
          setName('');
          setAvailableSimulations(0);
          setStartDate('');
          setEndDate('');
          return false;
        }

        setHasLicense(true);
        setName(license.name);
        setAvailableSimulations(license.available_simulations);
        setStartDate(license.start_date);
        setEndDate(license.end_date);
        return true;
      })
      .catch((error) => error);
  };

  useEffect(() => {
    if (storedAccessToken()) {
      setLicenseData();
    }
  }, []);
  return (
    <LicenseDataContext.Provider
      value={{
        setLicenseData,
        hasLicense,
        name,
        availableSimulations,
        startDate,
        endDate,
      }}
    >
      {children}
    </LicenseDataContext.Provider>
  );
};

export const useLicenseDataContext = (): contextType => {
  const context = useContext(LicenseDataContext);

  if (context === undefined) {
    throw new Error('useLicenseDataContext must be used within a LicenseDataProvider');
  }

  return context;
};
export default LicenseDataProvider;
